import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { logger } from 'features/logger/logger';
import { fetchNetworkProbe } from './fetchNetworkProbe';

const PROBE_INTERVAL_MS = 10_000;

export const useSetupNetworkStatus = () => {
  const [initialized, setInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const probeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const stopProbing = useCallback(() => {
    if (probeIntervalRef.current !== null) {
      clearInterval(probeIntervalRef.current);
      probeIntervalRef.current = null;
    }
  }, []);

  const verifyWithProbe = useCallback(async () => {
    const probeResult = await fetchNetworkProbe();
    if (probeResult) {
      setIsOnline(true);
      stopProbing();
    }
    return probeResult;
  }, [stopProbing]);

  const startProbing = useCallback(() => {
    if (probeIntervalRef.current !== null) return;

    probeIntervalRef.current = setInterval(() => {
      void verifyWithProbe();
    }, PROBE_INTERVAL_MS);
  }, [verifyWithProbe]);

  const handleStatusChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        setIsOnline(true);
        stopProbing();
        return;
      }

      if (Capacitor.isNativePlatform()) {
        setIsOnline(false);
        return;
      }

      // On web, don't trust navigator.onLine=false — verify with fetch
      void verifyWithProbe().then(isActuallyOnline => {
        if (!mountedRef.current) return;
        if (!isActuallyOnline) {
          setIsOnline(false);
          startProbing();
        }
      });
    },
    [stopProbing, verifyWithProbe, startProbing]
  );

  useEffect(() => {
    const initialize = async () => {
      const status = await Network.getStatus();
      handleStatusChange(status.connected);
      setInitialized(true);
    };

    void initialize();
  }, [handleStatusChange]);

  useEffect(() => {
    let listenerHandle: { remove: () => void } | null = null;
    let cancelled = false;

    void Network.addListener('networkStatusChange', status => {
      logger.debug('Network status changed', { details: status });
      handleStatusChange(status.connected);
    }).then(handle => {
      if (cancelled) {
        void handle.remove();
      } else {
        listenerHandle = handle;
      }
    });

    return () => {
      mountedRef.current = false;
      cancelled = true;
      listenerHandle?.remove();
      stopProbing();
    };
  }, [handleStatusChange, stopProbing]);

  return {
    initialized,
    isOnline,
  };
};
